import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api, onSessionLost } from '../api/client';
import type { Me } from '../api/types';

type SessionValue = {
  me: Me | null;
  loading: boolean;
  reload: () => void;
  logout: () => Promise<void>;
  isStaff: boolean;
};

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMe(await api.get<Me>('/v1/me'));
    } catch (cause) {
      // Un 401 est le cas normal d'un visiteur non connecté, pas une anomalie.
      if (!(cause instanceof ApiError) || cause.status !== 401) {
        console.warn('Session indisponible', cause);
      }
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Toute réponse 401 remet la session à zéro : l'interface bascule sur l'écran de
  // connexion au lieu d'enchaîner des erreurs incompréhensibles.
  useEffect(() => onSessionLost(() => setMe(null)), []);

  const logout = useCallback(async () => {
    try {
      await api.post('/v1/auth/logout');
    } finally {
      setMe(null);
    }
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      loading,
      reload: () => void load(),
      logout,
      isStaff: me?.role === 'STAFF' || me?.role === 'ADMIN',
    }),
    [me, loading, load, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession doit être utilisé dans un SessionProvider');
  return context;
}
